import type { Lci3Report } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import type { Lci3DimensionTableData } from '@features/lci-report/models/lci3-types';
import type { Slide, TableCell } from '@features/powerpoint';
import type { Subdimension } from '@features/reports/subdomains/visitation';
import type { UseCalculatedFieldsReturn } from '@features/campaigns';
import type { FormSchema } from '../../../../../../components/form-schema';
import { formatters } from '@utils/formattersV2';
import { MISSING_DOLLAR_VALUE } from '@utils/formatters';
import { themeV2 } from '../../../../../../domain/theme-v2';
import { DEFAULT_FONT } from '../../../../../../utils/constants-v2';
import { generateTableV2 } from '../../../../../../utils/build-table-v2';
import { contentSlideHeaderV2 } from '../../../../../../utils/slide-header-v2';
import { groupDimensionData } from './domain/flatten-dimension-data';

const SUBDIMENSIONS_PER_SLIDE = 8;

const TABLE_HEADER_OPTIONS: TableCell['options'] = {
  color: themeV2.colors.metricBlue,
  bold: true,
  fontFace: DEFAULT_FONT,
};

const visitLiftColumnLabels: TableCell[] = [
  { text: 'Dimension', options: TABLE_HEADER_OPTIONS },
  { text: 'Total Impressions', options: TABLE_HEADER_OPTIONS },
  { text: 'Unique Users Reached', options: TABLE_HEADER_OPTIONS },
  { text: 'Total Projected Visits', options: TABLE_HEADER_OPTIONS },
  { text: 'Incremental Visits', options: TABLE_HEADER_OPTIONS },
  { text: 'Incremental Lift', options: TABLE_HEADER_OPTIONS },
  { text: 'Cost Per Visit', options: TABLE_HEADER_OPTIONS },
  { text: 'Cost Per Incremental Visit', options: TABLE_HEADER_OPTIONS },
];

const formatRow = (row: Subdimension): TableCell[] => [
  { text: row.value },
  {
    text: formatters.impressions(row.visitMetrics.impressions, { full: true }),
  },
  { text: formatters.users(row.visitMetrics.uniqueUsers, { full: true }) },
  {
    text: formatters.visits(row.visitMetrics.projectedVisits, { full: true }),
  },
  {
    text: formatters.visits(row.visitMetrics.incrementalVisits, { full: true }),
  },
  { text: formatters.lift(row.visitMetrics.incrementalLift) },
  { text: MISSING_DOLLAR_VALUE },
  { text: MISSING_DOLLAR_VALUE },
];

const formatOverallRow = (
  overallVisitMetrics: Lci3Report['visitMetrics'],
  calculatedFields: VisitLiftSummaryTableV2Props['calculatedFields']
): TableCell[] => [
  { text: 'Overall', options: { bold: true } },
  {
    text: formatters.impressions(overallVisitMetrics.impressions, {
      full: true,
    }),
  },
  {
    text: formatters.users(overallVisitMetrics.uniqueUsers, { full: true }),
  },
  {
    text: formatters.visits(overallVisitMetrics.projectedVisits, {
      full: true,
    }),
  },
  {
    text: formatters.visits(overallVisitMetrics.incrementalVisits, {
      full: true,
    }),
  },
  { text: formatters.lift(overallVisitMetrics.incrementalLift) },
  {
    text:
      calculatedFields.costPerVisit === null
        ? MISSING_DOLLAR_VALUE
        : formatters.currency(calculatedFields.costPerVisit, {
            full: true,
            decimals: 2,
          }),
  },
  {
    text:
      calculatedFields.costPerIncrementalVisit === null
        ? MISSING_DOLLAR_VALUE
        : formatters.currency(calculatedFields.costPerIncrementalVisit, {
            full: true,
            decimals: 2,
          }),
  },
];

type VisitLiftSummaryTableV2Props = {
  slideMaster: string;
  overallVisitMetrics: Lci3Report['visitMetrics'];
  dimensionTableData: Lci3DimensionTableData;
  calculatedFields: Pick<
    UseCalculatedFieldsReturn['data'],
    'costPerVisit' | 'costPerIncrementalVisit'
  >;
  customFields: FormSchema['dimensions'];
};

export const visitLiftSummaryTableV2 = (
  props: VisitLiftSummaryTableV2Props
): Slide[] => {
  const dimensionGroups = groupDimensionData(props.dimensionTableData);
  const overallRow = formatOverallRow(
    props.overallVisitMetrics,
    props.calculatedFields
  );

  return dimensionGroups.flatMap(group => {
    const subdimensionRows = group.subdimensions.map(row => formatRow(row));
    const totalPages = Math.max(
      1,
      Math.ceil(subdimensionRows.length / SUBDIMENSIONS_PER_SLIDE)
    );

    const customHeader =
      props.customFields[group.slug as keyof FormSchema['dimensions']]
        .visitsTableHeader;

    return Array.from({ length: totalPages }, (_, pageIndex) => {
      const startIndex = pageIndex * SUBDIMENSIONS_PER_SLIDE;
      const pageRows = subdimensionRows.slice(
        startIndex,
        startIndex + SUBDIMENSIONS_PER_SLIDE
      );

      const defaultTitle = `Visit Lift Summary Table - By ${group.dimensionName} (${pageIndex + 1}/${totalPages})`;
      const title = customHeader.length > 0 ? customHeader : defaultTitle;

      return {
        slideMaster: props.slideMaster,
        tables: [
          generateTableV2(visitLiftColumnLabels, [overallRow, ...pageRows], {
            w: 12.35,
            x: 0.36,
            y: 1.17,
            rowH: 0.35,
            fontSize: 10,
          }),
        ],
        texts: [contentSlideHeaderV2(title)],
      };
    });
  });
};
